import { isEmpty } from 'lodash-es';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useToggle } from 'react-use';
import { EventAccordionItemProps } from 'src/components/Accordions';
import { InventoryEventFulfillmentRulesSection } from 'src/components/Listings/InventoryEventPage/Sections/InventoryEventFulfillmentRulesSection';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { Content } from 'src/contexts/ContentContext';
import { InputPriceFocusProvider } from 'src/contexts/InputPriceFocusContext/InputPriceFocusContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { vars } from 'src/core/themes';
import { Button, Stack, TooltipPopover } from 'src/core/ui';
import * as Tabs from 'src/core/ui/Tabs';
import { BasicDialogProps, useBasicDialog } from 'src/hooks/useBasicDialog';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { ColumnSettingsModal } from 'src/modals/ColumnSettings/ColumnSettings';
import { LayoutIcon } from 'src/svgs';
import { InfoOutlineIcon, PlusIcon } from 'src/svgs/Viagogo';
import { CustomListingColumn } from 'src/utils/columns/inventory/inventoryCustomColumnUtils.types';
import { InventoryDeeplinkQueryParam } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { getDeepLinkIdFromUrl } from 'src/utils/deepLinkUtils';
import { getListingDetailsModalConfigWithDeepLink } from 'src/utils/inventoryUtils';
import { SectionType } from 'src/utils/types/sectionType';
import {
  EventWithData,
  Feature,
  Listing,
  ListingDetailDataField,
  ListingDetails,
  Permission,
  UserSetting,
} from 'src/WebApiController';

import * as styles from './InventoryEventPage.css';
import { InventoryEventTabs } from './InventoryEventTabs';
import { InventoryEventDashboardSection } from './Sections/InventoryEventDashboardSection';
import { InventoryEventEditMapSection } from './Sections/InventoryEventEditMapSection';
import { InventoryEventFulfillmentSection } from './Sections/InventoryEventFulfillmentSection';
import { InventoryEventInfoSection } from './Sections/InventoryEventInfoSection';
import { InventoryEventTransactionsSection } from './Sections/InventoryEventTransactionsSection';
import { InventoryPricingABTestSection } from './Sections/InventoryPricingABTestSection';
import { ManualPricingSection } from './Sections/ManualPricingSection';
import { TagsSection } from './Sections/TagsSection';
import { SellThroughRateSection } from './SellThroughRateSection';
import { TabWithStarIcon } from './TabWithStarIcon';

type InventoryEventPageContentProps = {
  accordionItemProps?: EventWithData;
  inventoryAccordionItem?: EventAccordionItemProps<Listing>;
  selectedListingIds: number[];
  setSelectedListingIds: Dispatch<SetStateAction<number[]>>;
  activeTab: InventoryEventTabs;
  setActiveTab: (tab: InventoryEventTabs) => void;
};

export const InventoryEventPageContent: React.FC<
  InventoryEventPageContentProps
> = ({
  accordionItemProps,
  inventoryAccordionItem,
  selectedListingIds,
  setSelectedListingIds,
  activeTab,
  setActiveTab,
}) => {
  const hasPricingV2DemandDashboard = useUserHasFeature(
    Feature.PricingV2DemandDashboard
  );
  const hasConsignorMasterUI = useUserHasConsignorMasterUI();

  const { setModal } = useContext(ModalContext);
  const { posEntity: listing, setActivePosEntity } =
    useActivePosEntityContext<ListingDetails>();

  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);
  const hasRecentTransactionsAccess = useUserHasFeature(
    Feature.InventoryEventRecentTransactions
  );
  const hasFulfillmentRulesEventLevelFeature = useUserHasFeature(
    Feature.FulfillmentRulesEventLevel
  );
  const hasEditPricingPermission = useUserHasAnyOfPermissions(
    Permission.Inventory_SetPrice,
    Permission.Inventory_SetPriceOnIsPricerOf,
    Permission.Inventory_SetPriceOnPurchasedBy
  );
  const hasPricingABTestFeature = useUserHasFeature(Feature.PricingABTest);
  const hasEditMapInListingTab = useUserHasFeature(Feature.EditMapInListingTab);

  const {
    value: defaultInventoryTabUserSetting,
    setUserSetting: setDefaultInventoryTabUserSetting,
    isLoading: defaultInventoryTabLoading,
  } = useServerUserSetting<InventoryEventTabs>({
    id: UserSetting.InventoryDefaultTab,
  });

  const activeId = getDeepLinkIdFromUrl(
    InventoryDeeplinkQueryParam,
    window.location.href
  );

  const createExperimentDialog = useBasicDialog();
  const isMobile = useMatchMedia('mobile');

  useEffect(() => {
    if (activeId?.length && !listing) {
      setActivePosEntity(parseInt(activeId), undefined, true, [
        ListingDetailDataField.Basic,
      ]);
      setModal(getListingDetailsModalConfigWithDeepLink(isMobile, activeId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const [defaultTab, setDefaultTab] = useState<InventoryEventTabs | undefined>(
    defaultInventoryTabUserSetting
  );

  useEffect(() => {
    if (
      defaultInventoryTabUserSetting != null &&
      defaultInventoryTabUserSetting !== activeTab &&
      defaultInventoryTabUserSetting !== InventoryEventTabs.None &&
      defaultInventoryTabLoading === false
    ) {
      setActiveTab(defaultInventoryTabUserSetting);
    }
    if (
      defaultInventoryTabUserSetting != null &&
      defaultInventoryTabUserSetting !== defaultTab &&
      defaultInventoryTabUserSetting !== InventoryEventTabs.None &&
      defaultInventoryTabLoading === false
    ) {
      setDefaultTab(defaultInventoryTabUserSetting);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultInventoryTabLoading, defaultInventoryTabUserSetting]);

  const onSetDefaultTabClick = useCallback(
    async (tab: InventoryEventTabs) => {
      if (tab !== activeTab && tab !== InventoryEventTabs.None) {
        setActiveTab(tab);
      }
      if (tab !== defaultTab) {
        setDefaultTab(tab);
      }
      setDefaultInventoryTabUserSetting(tab);
    },
    [activeTab, defaultTab, setActiveTab, setDefaultInventoryTabUserSetting]
  );

  const event = accordionItemProps?.event!;

  return (
    <Tabs.Root
      value={activeTab}
      onValueChange={(value) => {
        setActiveTab(value as InventoryEventTabs);
      }}
      className={styles.tabsSection}
    >
      <Stack
        justifyContent="spaceBetween"
        alignItems="center"
        gap="m"
        className={styles.tabsHeader}
      >
        <Tabs.List
          rootClassName={styles.tabsList}
          style={{ padding: `${vars.spacing.m} ${vars.spacing['xl']}` }}
        >
          {hasPricingV2DemandDashboard && !hasConsignorMasterUI && (
            <Tabs.Trigger value={InventoryEventTabs.Dashboard}>
              <TabWithStarIcon
                onIconClick={onSetDefaultTabClick}
                defaultTab={defaultTab}
                tabType={InventoryEventTabs.Dashboard}
              >
                <Content id={ContentId.Dashboard} />
              </TabWithStarIcon>
            </Tabs.Trigger>
          )}
          <Tabs.Trigger value={InventoryEventTabs.Info}>
            <TabWithStarIcon
              onIconClick={onSetDefaultTabClick}
              defaultTab={defaultTab}
              tabType={InventoryEventTabs.Info}
            >
              <Content id={ContentId.Inventory} />
            </TabWithStarIcon>
          </Tabs.Trigger>
          <Tabs.Trigger value={InventoryEventTabs.Tags}>
            <TabWithStarIcon
              onIconClick={onSetDefaultTabClick}
              defaultTab={defaultTab}
              tabType={InventoryEventTabs.Tags}
            >
              <Content id={ContentId.Tags} />
            </TabWithStarIcon>
          </Tabs.Trigger>
          {hasAutoPricingFeature && (
            <Tabs.Trigger value={InventoryEventTabs.SellThroughRate}>
              <TabWithStarIcon
                onIconClick={onSetDefaultTabClick}
                defaultTab={defaultTab}
                tabType={InventoryEventTabs.SellThroughRate}
              >
                <Content id={ContentId.SellThroughRate} />
              </TabWithStarIcon>
            </Tabs.Trigger>
          )}
          {hasEditPricingPermission && (
            <Tabs.Trigger value={InventoryEventTabs.ManualPricing}>
              <TabWithStarIcon
                onIconClick={onSetDefaultTabClick}
                defaultTab={defaultTab}
                tabType={InventoryEventTabs.ManualPricing}
              >
                <Content id={ContentId.StubHubListings} />
              </TabWithStarIcon>
            </Tabs.Trigger>
          )}

          {hasRecentTransactionsAccess && (
            <Tabs.Trigger value={InventoryEventTabs.Transactions}>
              <TabWithStarIcon
                onIconClick={onSetDefaultTabClick}
                defaultTab={defaultTab}
                tabType={InventoryEventTabs.Transactions}
              >
                <Content id={ContentId.Transactions} />
              </TabWithStarIcon>
            </Tabs.Trigger>
          )}
          {(hasFulfillmentRulesEventLevelFeature ||
            accordionItemProps?.event?.viagId != null) && (
            <Tabs.Trigger value={InventoryEventTabs.Fulfillment}>
              <TabWithStarIcon
                // Only show if there's feature flag and this event has eventId
                onIconClick={onSetDefaultTabClick}
                defaultTab={defaultTab}
                tabType={InventoryEventTabs.Fulfillment}
              >
                <Content id={ContentId.Delivery} />
              </TabWithStarIcon>
            </Tabs.Trigger>
          )}
          {hasPricingABTestFeature && !hasConsignorMasterUI && (
            <Tabs.Trigger value={InventoryEventTabs.PricingABTest}>
              <TabWithStarIcon
                onIconClick={onSetDefaultTabClick}
                defaultTab={defaultTab}
                tabType={InventoryEventTabs.PricingABTest}
              >
                <Stack alignItems="center">
                  <Content id={ContentId.FlashSales} />
                  <TooltipPopover
                    triggerContent={
                      <InfoOutlineIcon
                        fill={
                          activeTab === InventoryEventTabs.PricingABTest
                            ? vars.color.textBrand
                            : undefined
                        }
                        withHoverEffect
                        size={vars.iconSize.m}
                      />
                    }
                  >
                    <Content id={ContentId.FlashSaleInfo} />
                  </TooltipPopover>
                </Stack>
              </TabWithStarIcon>
            </Tabs.Trigger>
          )}
        </Tabs.List>
        <InventoryHeaderAction
          activeTab={activeTab}
          createExperimentDialog={createExperimentDialog}
        />
      </Stack>

      {hasPricingV2DemandDashboard && (
        <Tabs.Content value={InventoryEventTabs.Dashboard}>
          <InventoryEventDashboardSection
            accordionItemProps={accordionItemProps}
          />
        </Tabs.Content>
      )}
      <Tabs.Content
        value={InventoryEventTabs.Info}
        className={styles.tabContent}
      >
        <InventoryEventInfoSection
          accordionItemProps={accordionItemProps}
          inventoryAccordionItem={inventoryAccordionItem}
          selectedListingIds={selectedListingIds}
          setSelectedListingIds={setSelectedListingIds}
        />
      </Tabs.Content>
      <Tabs.Content value={InventoryEventTabs.Tags}>
        <TagsSection eventId={event.virtId} />
      </Tabs.Content>
      {hasAutoPricingFeature && (
        <Tabs.Content value={InventoryEventTabs.SellThroughRate}>
          <SellThroughRateSection event={event} />
        </Tabs.Content>
      )}
      {hasEditPricingPermission && (
        <Tabs.Content
          value={InventoryEventTabs.ManualPricing}
          className={styles.tabContent}
        >
          <InputPriceFocusProvider
            disablePagination
            pageSize={accordionItemProps?.counts?.listCnt ?? 0}
          >
            <ManualPricingSection accordionItemProps={accordionItemProps} />
          </InputPriceFocusProvider>
        </Tabs.Content>
      )}
      {hasRecentTransactionsAccess && (
        <Tabs.Content value={InventoryEventTabs.Transactions}>
          <InventoryEventTransactionsSection
            accordionItemProps={accordionItemProps}
            inventoryAccordionItem={inventoryAccordionItem}
          />
        </Tabs.Content>
      )}

      <Tabs.Content value={InventoryEventTabs.Fulfillment}>
        {accordionItemProps?.event?.viagId != null && (
          <InventoryEventFulfillmentSection
            accordionItemProps={accordionItemProps}
            inventoryAccordionItem={inventoryAccordionItem}
          />
        )}
        {accordionItemProps?.event?.viagId != null &&
          hasFulfillmentRulesEventLevelFeature && <hr />}
        {hasFulfillmentRulesEventLevelFeature && (
          <InventoryEventFulfillmentRulesSection
            accordionItemProps={accordionItemProps}
            inventoryAccordionItem={inventoryAccordionItem}
          />
        )}
      </Tabs.Content>

      {hasFulfillmentRulesEventLevelFeature && (
        <Tabs.Content value={InventoryEventTabs.FulfillmentRules}>
          <InventoryEventFulfillmentRulesSection
            accordionItemProps={accordionItemProps}
            inventoryAccordionItem={inventoryAccordionItem}
          />
        </Tabs.Content>
      )}

      {hasEditMapInListingTab && accordionItemProps?.event?.viagId != null && (
        <Tabs.Content value={InventoryEventTabs.EditMap}>
          <InventoryEventEditMapSection event={event} />
        </Tabs.Content>
      )}

      {hasPricingABTestFeature &&
        accordionItemProps?.event?.viagId != null &&
        !isEmpty(accordionItemProps?.entities?.listings) && (
          <Tabs.Content value={InventoryEventTabs.PricingABTest}>
            <InventoryPricingABTestSection
              event={event}
              listings={accordionItemProps.entities.listings ?? []}
              createExperimentDialog={createExperimentDialog}
            />
          </Tabs.Content>
        )}
    </Tabs.Root>
  );
};

type InventoryHeaderActionProps = {
  activeTab: InventoryEventTabs;
  createExperimentDialog: BasicDialogProps;
};
export const InventoryHeaderAction: React.FC<InventoryHeaderActionProps> = ({
  activeTab,
  createExperimentDialog,
}) => {
  const [isColumnModalOpen, toggleColumnModal] = useToggle(false);

  const onColumnSettingButtonClickHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!isColumnModalOpen) toggleColumnModal(true);
    },
    [isColumnModalOpen, toggleColumnModal]
  );

  return (
    <div>
      {[InventoryEventTabs.Info, InventoryEventTabs.ManualPricing].includes(
        activeTab
      ) && (
        <Stack width="full" alignItems="end">
          <Button
            variant="textPlain"
            onClick={onColumnSettingButtonClickHandler}
          >
            <LayoutIcon size={vars.iconSize.m} />
            <Content id={ContentId.Columns} />
          </Button>
        </Stack>
      )}
      {activeTab === InventoryEventTabs.PricingABTest && (
        <Stack
          width="full"
          justifyContent="end"
          style={{ whiteSpace: 'nowrap' }}
        >
          <Button
            variant="textPlain"
            onClick={() => createExperimentDialog.launchDialog()}
          >
            <PlusIcon size={vars.iconSize.m} />
            <Content id={ContentId.FlashSale} />
          </Button>
        </Stack>
      )}

      {isColumnModalOpen && (
        <ColumnSettingsModal<CustomListingColumn>
          onClose={toggleColumnModal}
          sectionType={SectionType.Listings}
        />
      )}
    </div>
  );
};
